import { ArrowRight, Building2, Contact, Layers, Mail, RefreshCw } from "lucide-react";

interface MetricProps {
  label: string;
  value: string | number;
  note?: string;
}

function Metric({ label, value, note }: MetricProps) {
  return (
    <div className="rounded-xl border border-[#dfe5df] bg-[#fffefb] p-4 shadow-[0_7px_22px_rgba(23,79,57,0.04)]">
      <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#647068]">{label}</p>
      <p className="mt-2 font-heading text-3xl font-bold text-[#17221c]">{value}</p>
      {note ? <p className="mt-1 font-body text-[10px] leading-4 text-[#7a847e]">{note}</p> : null}
    </div>
  );
}

interface QuickLinkProps {
  icon: typeof Building2;
  title: string;
  copy: string;
  action: string;
  onClick: () => void;
}

function QuickLink({ icon: Icon, title, copy, action, onClick }: QuickLinkProps) {
  return (
    <article className="rounded-xl border border-[#dfe5df] bg-[#fffefb] p-5">
      <Icon className="h-5 w-5 text-[#277454]" />
      <h2 className="mt-4 font-heading text-lg font-bold text-[#17221c]">{title}</h2>
      <p className="mt-1 min-h-12 font-body text-sm leading-6 text-[#5f6963]">{copy}</p>
      <button type="button" onClick={onClick} className="mt-3 inline-flex items-center gap-1 font-body text-xs font-bold text-[#277454] hover:text-[#174f39]">
        {action} <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </article>
  );
}

export interface PlatformDashboardViewProps {
  adminName: string;
  roleLabel: string;
  orgs: number | null;
  studios: number | null;
  endUsers: number | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onViewOrganizations: () => void;
  onViewWorkspaces: () => void;
  onViewApps: () => void;
  onViewCommunications: () => void;
}

export function PlatformDashboardView({
  adminName,
  roleLabel,
  orgs,
  studios,
  endUsers,
  loading,
  error,
  onRefresh,
  onViewOrganizations,
  onViewWorkspaces,
  onViewApps,
  onViewCommunications,
}: PlatformDashboardViewProps) {
  const countValue = (value: number | null) => (loading ? "Loading" : error || value === null ? "Unavailable" : value);

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.17em] text-[#277454]">
            Client Portal / Platform Dashboard
          </p>
          <h1 className="mt-2 font-heading text-4xl font-bold text-[#17221c] sm:text-5xl">
            Welcome, {adminName || "Administrator"}
          </h1>
          <p className="mt-2 max-w-2xl font-body text-sm leading-6 text-[#5f6963]">
            Manage your client organizations, workspaces and communications.
          </p>
        </div>
        <div className="self-start rounded-lg border border-[#bad0c0] bg-[#f0f7f2] px-3 py-2 sm:self-auto">
          <p className="font-body text-[9px] font-extrabold uppercase tracking-wide text-[#277454]">Platform role</p>
          <p className="mt-0.5 font-body text-xs font-bold text-[#385344]">{roleLabel}</p>
        </div>
      </header>

      <section aria-label="Platform totals" className="grid gap-3 sm:grid-cols-3">
        <Metric label="Organizations" value={countValue(orgs)} note="All client organizations" />
        <Metric label="Workspaces" value={countValue(studios)} note="Across every organization" />
        <Metric label="User records" value={countValue(endUsers)} note="Studio users, workshop contacts and other contacts" />
      </section>

      {!error ? (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 font-body text-xs font-bold text-[#277454] hover:text-[#174f39] disabled:opacity-50"
          >
            <RefreshCw className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
            Refresh totals
          </button>
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="mt-4 flex flex-col gap-3 rounded-xl border border-[#e4b8af] bg-[#fff3f0] p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-body text-sm text-[#863c32]">{error} Platform totals are unavailable.</p>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 self-start font-body text-xs font-bold text-[#863c32] disabled:opacity-50"
          >
            <RefreshCw className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
            Retry
          </button>
        </div>
      ) : null}

      <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <QuickLink
          icon={Building2}
          title="Organizations"
          copy="Review every client organization, its owner and status."
          action="View organizations"
          onClick={onViewOrganizations}
        />
        <QuickLink
          icon={Layers}
          title="Workspaces"
          copy="Open a workspace to manage its users, agents and status."
          action="View workspaces"
          onClick={onViewWorkspaces}
        />
        <QuickLink
          icon={Contact}
          title="Apps"
          copy="Manage the platform app catalog and its org assignments."
          action="View apps"
          onClick={onViewApps}
        />
        <QuickLink
          icon={Mail}
          title="Communications"
          copy="Campaigns, templates, automations and drip sequences."
          action="View communications"
          onClick={onViewCommunications}
        />
      </section>
    </div>
  );
}
